import { useState, useRef, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { PieChart, Pie, Cell, ResponsiveContainer } from 'recharts';
import { useDataContext } from '../../../context/DataContext';
import '../../../styles/components/node-details-popup.css';
import { ReactComponent as PopupIcon } from '../../../Assets/icons/modal_icon.svg';

const LOS_COLORS = {
  'A': '#8FC975',
  'B': '#AED361',
  'C': '#F6ED60',
  'D': '#FDB64E',
  'E': '#EC1D23',
  'F': '#470D16'
};

const LOS_GRADES = ['A', 'B', 'C', 'D', 'E', 'F'];
const TOOLTIP_X_OFFSET = -25;

const NodeDetailsPopup = ({
  isOpen,
  onClose,
  nodeData,
  sectionData,
  sharedTooltipState,
  onTooltipChange,
  isInComparisonMode,
  onStreetHighlight
}) => {
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });

  const [localSelectedSection, setLocalSelectedSection] = useState(null);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
  const [tooltipDragOffset, setTooltipDragOffset] = useState({ x: 0, y: 0 });
  const [isTooltipDragging, setIsTooltipDragging] = useState(false);
  const [tooltipDragStart, setTooltipDragStart] = useState({ x: 0, y: 0 });

  const tooltipRef = useRef(null);
  const chartContainerRef = useRef(null);
  const isUserInitiatedRef = useRef(false);
 
  const { dataJson } = useDataContext();

  const selectedSection = localSelectedSection;
  const showTooltip = localSelectedSection !== null;

  const setSelectedSectionAndNotify = useCallback((section) => {
    // Helper to get a unique identifier for a section
    const getSectionId = (s) => {
      if (!s) return null;
      return s.sectionId || s.sectionData?.idSec || s.sectionData?.nomeSec || s.name;
    };

    const currentId = getSectionId(localSelectedSection);
    const newId = getSectionId(section);

    // If clicking the same section, toggle it off
    if (section !== null && currentId !== null && currentId === newId) {
      setLocalSelectedSection(null);
      if (isInComparisonMode && onTooltipChange) {
        onTooltipChange({ selectedSectionId: null, showTooltip: false });
      }
      // Clear street highlight when deselecting
      if (onStreetHighlight) {
        onStreetHighlight(null);
      }
      return;
    }

    // Mark as user-initiated to prevent sync interference
    isUserInitiatedRef.current = true;

    // Set the new selection immediately
    setLocalSelectedSection(section);

    if (isInComparisonMode && onTooltipChange) {
      if (section) {
        const sectionId = section.sectionId ||
          section.sectionData?.idSec ||
          section.sectionData?.nomeSec;
        onTooltipChange({ selectedSectionId: sectionId, showTooltip: true });
      } else {
        onTooltipChange({ selectedSectionId: null, showTooltip: false });
      }
    }

    // Highlight the street on the map
    if (onStreetHighlight) {
      if (section) {
        // Extract the OSM ID from sectionData.idSec (format: "12345.0" -> "12345")
        const idSec = section.sectionData?.idSec;
        const osmId = idSec ? String(idSec).split('.')[0] : null;
        onStreetHighlight({
          osmId: osmId,
          sectionId: section.sectionId,
          sectionData: section.sectionData
        });
      } else {
        onStreetHighlight(null);
      }
    }

    // Reset user-initiated flag after a short delay
    setTimeout(() => {
      isUserInitiatedRef.current = false;
    }, 100);
  }, [localSelectedSection, isInComparisonMode, onTooltipChange, onStreetHighlight]);

  // Sync with shared state in comparison mode
  useEffect(() => {
    // Don't sync if this was a user-initiated action
    if (isUserInitiatedRef.current) {
      return;
    }

    if (isInComparisonMode && sharedTooltipState) {
      if (sharedTooltipState.showTooltip && sharedTooltipState.selectedSectionId && sectionData) {
        const matchingSection = sectionData.find(section =>
          section.sectionId === sharedTooltipState.selectedSectionId ||
          section.sectionData?.idSec === sharedTooltipState.selectedSectionId ||
          section.sectionData?.nomeSec === sharedTooltipState.selectedSectionId
        );

        if (matchingSection && (!localSelectedSection || localSelectedSection.sectionId !== matchingSection.sectionId)) {
          setLocalSelectedSection(matchingSection);

          setTimeout(() => {
            if (chartContainerRef.current) {
              const containerRect = chartContainerRef.current.getBoundingClientRect();
              setTooltipPosition({
                x: TOOLTIP_X_OFFSET,
                y: containerRect.height / 2
              });
            }
          }, 10);
        } else if (!matchingSection) {
          setLocalSelectedSection(null);
        }
      } else if (!sharedTooltipState.showTooltip) {
        setLocalSelectedSection(null);
      }
    }
  }, [isInComparisonMode, sharedTooltipState, sectionData, localSelectedSection]);

  // Reset selected section when popup is closed
  useEffect(() => {
    if (!isOpen) {
      setLocalSelectedSection(null);
    }
  }, [isOpen]);

  // Click outside handler for tooltip
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Check if click is on a pie cell (Recharts SVG element)
      const isPieClick = event.target.closest('.recharts-pie') ||
        event.target.closest('.recharts-pie-sector') ||
        event.target.classList?.contains('pie-cell') ||
        event.target.closest('path[class*="recharts"]');

      if (isPieClick) {
        // Don't deselect - let the pie onClick handler manage selection
        return;
      }

      if (tooltipRef.current && !tooltipRef.current.contains(event.target) &&
        chartContainerRef.current && !chartContainerRef.current.contains(event.target)) {
        setSelectedSectionAndNotify(null);
      }
    };

    if (showTooltip) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showTooltip, setSelectedSectionAndNotify]);

  const handleDragStart = useCallback((e) => {
    if (e.target.closest('.popup-header')) {
      setIsDragging(true);
      setDragStart({
        x: e.clientX - position.x,
        y: e.clientY - position.y
      });
    }
  }, [position]);

  const handleDragMove = useCallback((e) => {
    if (isDragging) {
      setPosition({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y
      });
    }
  }, [isDragging, dragStart]);

  const handleDragEnd = useCallback(() => {
    setIsDragging(false);
  }, []);

  const handleTooltipDragStart = useCallback((e) => {
    if (!e.target.closest('button')) {
      e.stopPropagation();
      setIsTooltipDragging(true);
      setTooltipDragStart({
        x: e.clientX - tooltipDragOffset.x,
        y: e.clientY - tooltipDragOffset.y
      });
    }
  }, [tooltipDragOffset]);

  const handleTooltipDragMove = useCallback((e) => {
    if (isTooltipDragging) {
      setTooltipDragOffset({
        x: e.clientX - tooltipDragStart.x,
        y: e.clientY - tooltipDragStart.y
      });
    }
  }, [isTooltipDragging, tooltipDragStart]);

  const handleTooltipDragEnd = useCallback(() => {
    setIsTooltipDragging(false);
  }, []);

  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleDragMove);
      document.addEventListener('mouseup', handleDragEnd);
      return () => {
        document.removeEventListener('mousemove', handleDragMove);
        document.removeEventListener('mouseup', handleDragEnd);
      };
    }
  }, [isDragging, handleDragMove, handleDragEnd]);

  useEffect(() => {
    if (isTooltipDragging) {
      document.addEventListener('mousemove', handleTooltipDragMove);
      document.addEventListener('mouseup', handleTooltipDragEnd);
      return () => {
        document.removeEventListener('mousemove', handleTooltipDragMove);
        document.removeEventListener('mouseup', handleTooltipDragEnd);
      };
    }
  }, [isTooltipDragging, handleTooltipDragMove, handleTooltipDragEnd]);

  if (!isOpen || !nodeData) return null;

  const chartData = sectionData || [
    { name: 'A', value: 15, color: LOS_COLORS.A },
    { name: 'B', value: 20, color: LOS_COLORS.B },
    { name: 'C', value: 35, color: LOS_COLORS.C },
    { name: 'D', value: 20, color: LOS_COLORS.D },
    { name: 'E', value: 10, color: LOS_COLORS.E },
    { name: 'F', value: 5, color: LOS_COLORS.F }
  ].filter(item => item.value > 0);

  const getLOSGrade = () => {
    if (nodeData.losNode) return nodeData.losNode;
    const maxValue = Math.max(...chartData.map(d => d.value));
    const dominant = chartData.find(d => d.value === maxValue);
    return dominant ? dominant.name : 'A';
  };

  const getLOSColor = (grade) => LOS_COLORS[grade] || '#333';

  const getNodeType = () => {
    if (!nodeData.tipo) return 'N/A';
    if (nodeData.tipo === 'rotatoria') return 'Rotatoria';
    if (nodeData.tipo === 'semaforo') return 'Semaforo';
    return nodeData.tipo.charAt(0).toUpperCase() + nodeData.tipo.slice(1);
  };

  const onPieClick = (data, index) => {
    const clickedSection = chartData[index];
    if (clickedSection && chartContainerRef.current) {
      const containerRect = chartContainerRef.current.getBoundingClientRect();
      setTooltipPosition({
        x: TOOLTIP_X_OFFSET,
        y: containerRect.height / 2
      });
      setSelectedSectionAndNotify(clickedSection);
    }
  };

  return (
    <>
      <div
        className={`popup-container ${isDragging ? 'dragging' : ''}`}
        style={{
          transform: `translate(${position.x}px, ${position.y}px)`,
          transition: isDragging ? 'none' : 'transform 0.2s ease'
        }}
        onMouseDown={handleDragStart}
      >
        <div className="popup-header">
          <div className="popup-title">
            <div className="popup-icon-wrapper">
              <PopupIcon />
            </div>
            <div className="popup-title-text">
              <h3>{nodeData.intersection || nodeData.nome || 'Intersezione'}</h3>
              <span className="popup-scenario-label">{getNodeType()}</span>
            </div>
          </div>
          <button className="popup-close" onClick={onClose} aria-label="Close">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        <div className="popup-body">
          <div className="chart-container" ref={chartContainerRef}>
            <ResponsiveContainer width="100%" height={180}>
              <PieChart>
                <Pie
                  data={chartData}
                  cx="50%"
                  cy="50%"
                  innerRadius={50}
                  outerRadius={70}
                  startAngle={90}
                  endAngle={450}
                  dataKey="value"
                  paddingAngle={2}
                  onClick={onPieClick}
                >
                  {chartData.map((entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={entry.color}
                      stroke={entry.color}
                      strokeWidth={selectedSection && selectedSection.name === entry.name ? 3 : 0}
                      className="pie-cell"
                    />
                  ))}
                </Pie>
              </PieChart>
            </ResponsiveContainer>

            <div className="chart-center-label">
              <span className="los-grade" style={{ color: getLOSColor(getLOSGrade()) }}>
                {getLOSGrade()}
              </span>
            </div>
          </div>

          <div className="los-grade-legend">
            <div className="los-grade-legend-title">LOS Scale</div>
            <div className="los-grade-legend-items">
              {LOS_GRADES.map((grade) => (
                <div key={grade} className="los-grade-legend-item">
                  <span
                    className={`los-grade-box ${['C', 'D'].includes(grade) ? 'light-bg' : ''}`}
                    style={{ backgroundColor: getLOSColor(grade) }}
                  >
                    {grade}
                  </span>
                </div>
              ))}
            </div>
          </div>

          <div className="popup-metrics-section">
            <div className="popup-metric-item">
              <div className="popup-metric-icon">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="white">
                  <polyline points="20 6 9 17 4 12" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" fill="none" />
                </svg>
              </div>
              <span className="popup-metric-label">Ritardo</span>
              <span className="popup-metric-value">{nodeData.delayNode ? nodeData.delayNode.toFixed(1) : '8.9'}</span>
            </div>

            <div className="popup-metric-item">
              <div className="popup-metric-icon">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="white">
                  <polyline points="20 6 9 17 4 12" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" fill="none" />
                </svg>
              </div>
              <span className="popup-metric-label">Veicoli</span>
              <span className="popup-metric-value">{nodeData.totalVehicles.toFixed(2) || '904'}</span>
            </div>
          </div>
        </div>
      </div>

      {showTooltip && selectedSection && (
        <div
          ref={tooltipRef}
          className={`section-tooltip ${isTooltipDragging ? 'dragging' : ''}`}
          style={{
            position: 'fixed',
            left: `calc(${position.x}px + ${tooltipPosition.x}px + ${tooltipDragOffset.x}px)`,
            top: `calc(40px + ${position.y}px + ${tooltipPosition.y}px + ${tooltipDragOffset.y}px)`,
            transform: 'translate(-100%, -50%)',
            zIndex: 10000
          }}
          onMouseDown={handleTooltipDragStart}
        >
          <div className="tooltip-arrow"></div>
          <div className="tooltip-header">
            <span className="tooltip-title">
              {(() => {
                const section = selectedSection.sectionData;
                if (!section) return `Sezione: Grade ${selectedSection.name}`;
                
                const idSec = section.idSec;
                const sectionKey = String(idSec).includes('.') ? String(idSec) : `${idSec}.0`;
                const osmItem = dataJson?.geometryOSM?.find(item => 
                  String(item.id) === String(sectionKey) || 
                  String(item.id) === String(idSec)
                );
                
                return (osmItem?.display_name && osmItem.display_name !== null) ? 
                  osmItem.display_name : (osmItem?.name || section.nomeSec || `Sezione: Grade ${selectedSection.name}`);
              })()}
            </span>
            <button
              onClick={() => setSelectedSectionAndNotify(null)}
              className="tooltip-close"
              aria-label="Close tooltip"
            >
              ×
            </button>
          </div>
          <div className="tooltip-body">
            <div className="tooltip-metric">
              <span className="tooltip-label">Ritardo Sez.:</span>
              <span className="tooltip-value">
                {selectedSection.sectionData?.delaySec ? selectedSection.sectionData.delaySec.toFixed(1) : '0.0'}s
              </span>
            </div>
            <div className="tooltip-metric">
              <span className="tooltip-label">Veicoli:</span>
              <span className="tooltip-value">{selectedSection.sectionData?.count || '0'}</span>
            </div>
            <div className="tooltip-metric">
              <span className="tooltip-label">Ampiezza:</span>
              <span className="tooltip-value">
                {selectedSection.sectionData?.ampiezza ? parseFloat(selectedSection.sectionData.ampiezza).toFixed(1) : '0.0'}
              </span>
            </div>
            <div className="tooltip-metric">
              <span className="tooltip-label">LOS Grade:</span>
              <span className="tooltip-value-badge" style={{ backgroundColor: selectedSection.color }}>
                {selectedSection.name}
              </span>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

NodeDetailsPopup.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  nodeData: PropTypes.shape({
    idNodo: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    losNode: PropTypes.string,
    delayNode: PropTypes.number,
    totalVehicles: PropTypes.number,
    intersection: PropTypes.string,
    nome: PropTypes.string,
    tipo: PropTypes.string,
    sezioni: PropTypes.object
  }),
  sectionData: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      value: PropTypes.number,
      color: PropTypes.string,
      sectionData: PropTypes.object
    })
  ),
  sharedTooltipState: PropTypes.shape({
    selectedSectionId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    showTooltip: PropTypes.bool
  }),
  onTooltipChange: PropTypes.func,
  isInComparisonMode: PropTypes.bool,
  onStreetHighlight: PropTypes.func
};

export default NodeDetailsPopup;